import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, X } from 'lucide-react'
import type { JobSummary } from '../../types'
import * as db from '../../services/db'

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const seconds = Math.floor((now - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

interface ProjectDrawerProps {
  currentJobId: string
  onClose: () => void
}

export default function ProjectDrawer({ currentJobId, onClose }: ProjectDrawerProps) {
  const navigate = useNavigate()
  const [summaries, setSummaries] = useState<JobSummary[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)

  const fetchSummaries = (q?: string) => {
    setLoading(true)
    db.fetchJobSummaries(q)
      .then(setSummaries)
      .catch(() => undefined)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchSummaries()
  }, [])

  const handleSearch = (value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSummaries(value), 300)
  }

  const handleSelect = (id: string) => {
    navigate(`/workspace/${id}`)
    onClose()
  }

  return (
    <div className="h-full flex flex-col bg-[#0a0f1a] border-r border-cyan-500/15">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-cyan-500/15">
        <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">Projects</span>
        <button
          onClick={onClose}
          className="text-slate-600 hover:text-slate-300 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* New Job button */}
      <div className="px-3 pt-3 pb-2">
        <button
          onClick={() => navigate('/new-job')}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg font-mono text-xs border border-cyan-500/25 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400/50 transition-all"
        >
          <Plus className="w-3 h-3" /> New Project
        </button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-white/5 border border-cyan-500/15 focus-within:border-cyan-400/40 transition-colors">
          <Search className="w-3 h-3 text-slate-600 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search..."
            className="flex-1 bg-transparent font-mono text-xs text-slate-200 placeholder-slate-600 focus:outline-none"
          />
        </div>
      </div>

      {/* Job list */}
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {loading && summaries.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <span className="font-mono text-[10px] text-slate-600 animate-pulse">Loading...</span>
          </div>
        ) : summaries.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <span className="font-mono text-[10px] text-slate-600">No projects found</span>
          </div>
        ) : (
          <div className="space-y-0.5">
            {summaries.map(s => {
              const isActive = s.id === currentJobId
              return (
                <button
                  key={s.id}
                  onClick={() => handleSelect(s.id)}
                  className={`
                    w-full text-left px-2.5 py-2 rounded-lg transition-all duration-150
                    ${isActive
                      ? 'bg-cyan-500/10 border border-cyan-500/25'
                      : 'border border-transparent hover:bg-white/5 hover:border-cyan-500/10'
                    }
                  `}
                >
                  <div className="flex items-start justify-between gap-2 mb-0.5">
                    <span className={`font-mono text-xs truncate flex-1 ${isActive ? 'text-cyan-300' : 'text-slate-300'}`}>
                      {s.label}
                    </span>
                    <span className="font-mono text-[10px] text-slate-600 flex-shrink-0">
                      {timeAgo(s.updatedAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-slate-500">{s.material}</span>
                    <span className="font-mono text-[10px] text-slate-600">
                      {s.messageCount} msg{s.messageCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
